import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  Rating,
  Fade,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { FormatQuote, ChevronLeft, ChevronRight } from '@mui/icons-material';

const testimonials = [
  {
    name: 'Rajesh Sharma',
    role: 'Delhi',
    avatar: 'R',
    rating: 5,
    text: 'I ordered Shilajit Laddo from Veda & Vine for daily strength and honestly I can feel the difference. Energy levels improved, no heaviness, and the taste is surprisingly good compared to other health laddoos I’ve tried. It feels pure and homemade, not overly sweet. Perfect for anyone looking for natural stamina and immunity support.',
  },
  {
    name: 'Neha Gupta',
    role: 'Jaipur',
    avatar: 'N',
    rating: 4,
    text: 'The Panjiri from Veda & Vine reminds me of homemade panjiri my mother used to make. Fresh, aromatic, and very light on the stomach. I take it every morning with milk and it keeps me energetic throughout the day. Loved the quality and traditional taste — feels truly Ayurvedic and healthy.',
  },
  {
    name: 'Amit Mehta',
    role: 'Chandigarh',
    avatar: 'A',
    rating: 4,
    text: 'Really impressed with the service from Veda and Vine. Order was delivered on time, packaging was clean and premium, and the team was very responsive on WhatsApp for my queries. You can feel they genuinely care about quality and customers. Rare to see such honest service these days.',
  },
  {
    name: 'Sneha Kapoor',
    role: 'Fresno',
    avatar: 'S',
    rating: 5,
    text: 'I have tried multiple products from Veda and Vine and the quality has been consistently excellent. Taste feels natural, not artificial, and ingredients seem pure and traditional. It’s good to finally find a brand that focuses on health without compromising on flavor. My whole family is now using their products regularly.',
  },
  {
    name: 'Rahul Jain',
    role: 'Indore',
    avatar: 'R',
    rating: 5,
    text: 'Veda and Vine truly stands out for its purity, taste, and authenticity. Every product reflects quality and traditional care. I genuinely admire their range and would highly recommend them. I look forward to seeing more premium and health-focused products added to their collection.',
  },
];

const TestimonialsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentIndex, setCurrentIndex] = useState(0);
  const sliderRef = useRef(null);
  
  // Show 3 testimonials at a time on desktop, 1 on mobile
  const itemsToShow = isMobile ? 1 : 3;
  const maxIndex = Math.max(0, testimonials.length - itemsToShow);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  // Auto-scroll functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev >= maxIndex) {
          return 0;
        }
        return prev + 1;
      });
    }, 5000); // Auto-scroll every 5 seconds

    return () => clearInterval(interval);
  }, [maxIndex]);

  // Pause auto-scroll on hover
  const handleMouseEnter = () => {
    // Auto-scroll will be paused when user interacts
  };

  return (
    <Box
      id="testimonials"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #F8F6F1 0%, #E8E3D8 50%, #F5F2EB 100%)',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Fade in timeout={1000}>
          <Stack spacing={6}>
            {/* Section Header */}
            <Stack spacing={2} alignItems="center">
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: '0.2em',
                }}
              >
                Testimonials
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 700,
                  textAlign: 'center',
                }}
              >
                What Our Clients Say
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  bgcolor: 'primary.main',
                  borderRadius: 2,
                }}
              />
            </Stack>

            {/* Testimonials Slider Container */}
            <Box sx={{ position: 'relative', py: 4 }}>
              {/* Navigation Buttons */}
              {!isMobile && (
                <>
                  <IconButton
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    sx={{
                      position: 'absolute',
                      left: -60,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'white',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'primary.main', color: 'white' },
                      '&:disabled': { opacity: 0.3 },
                      zIndex: 2,
                    }}
              >
                    <ChevronLeft />
                  </IconButton>
                  <IconButton
                    onClick={handleNext}
                    disabled={currentIndex === maxIndex}
                    sx={{
                      position: 'absolute',
                      right: -60,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'white',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'primary.main', color: 'white' },
                      '&:disabled': { opacity: 0.3 },
                      zIndex: 2,
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                </>
              )}

              {/* Testimonials Slider */}
              <Box
                ref={sliderRef}
                sx={{
                  overflow: 'hidden',
                  position: 'relative',
                }}
                onMouseEnter={handleMouseEnter}
              >
                <Box
                  sx={{
                    display: 'flex',
                    transition: 'transform 0.5s ease-in-out',
                    transform: `translateX(-${currentIndex * (100 / itemsToShow)}%)`,
                    width: '100%',
                  }}
                >
                  {testimonials.map((testimonial, index) => (
                    <Box
                      key={index}
                      sx={{
                        minWidth: isMobile ? '100%' : `${100 / itemsToShow}%`,
                        px: isMobile ? 0 : 2,
                      }}
                    >
                      <Card
                        elevation={3}
                        sx={{
                          height: '100%',
                          minHeight: isMobile ? 400 : 450,
                          borderRadius: 3,
                          position: 'relative',
                          transition: 'all 0.3s ease',
                          mx: isMobile ? 0 : 1,
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: 6,
                          },
                        }}
                      >
                        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                          <Stack spacing={3}>
                            {/* Quote Icon */}
                            <FormatQuote
                              sx={{
                                fontSize: { xs: 40, md: 48 },
                                color: 'primary.main',
                                opacity: 0.3,
                              }}
                            />

                            {/* Rating */}
                            <Rating value={testimonial.rating} readOnly />

                            {/* Testimonial Text */}
                            <Typography
                              variant={isMobile ? "body2" : "body1"}
                              color="text.secondary"
                              sx={{ 
                                lineHeight: 1.8, 
                                fontStyle: 'italic',
                                fontSize: { xs: '0.875rem', md: '1rem' }
                              }}
                            >
                              "{testimonial.text}"
                            </Typography>

                            {/* Author Info */}
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Avatar
                                sx={{
                                  bgcolor: 'primary.main',
                                  width: { xs: 45, md: 50 },
                                  height: { xs: 45, md: 50 },
                                }}
                              >
                                {testimonial.avatar}
                              </Avatar>
                              <Box>
                                <Typography 
                                  variant="subtitle1" 
                                  fontWeight={600}
                                  sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' } }}
                                >
                                  {testimonial.name}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
                                >
                                  {testimonial.role}
                                </Typography>
                              </Box>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Dots Indicator */}
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                sx={{ mt: 4 }}
              >
                {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => handleDotClick(index)}
                    sx={{
                      width: currentIndex === index ? 12 : 8,
                      height: currentIndex === index ? 12 : 8,
                      borderRadius: '50%',
                      bgcolor: currentIndex === index ? 'primary.main' : 'grey.300',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: currentIndex === index ? 'primary.dark' : 'grey.400',
                      },
                    }}
                  />
                ))}
              </Stack>

              {/* Mobile Navigation */}
              {isMobile && (
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  sx={{ mt: 4 }}
                >
                  <IconButton
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    sx={{
                      bgcolor: 'white',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'primary.main', color: 'white' },
                      '&:disabled': { opacity: 0.3 },
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <IconButton
                    onClick={handleNext}
                    disabled={currentIndex === maxIndex}
                    sx={{
                      bgcolor: 'white',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'primary.main', color: 'white' },
                      '&:disabled': { opacity: 0.3 },
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                </Stack>
              )}
            </Box>
          </Stack>
        </Fade>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
